const backupUrl = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/backup`;

export type BackupScope =
  | "admin_users"
  | "app_users"
  | "paths"
  | "standalone_activities"
  | "settings"
  | "all";

export type BackupTableKey =
  | "auth_users"
  | "user_role"
  | "user_admin_profile"
  | "user_profile"
  | "user_path_progress"
  | "user_path_activity_progress"
  | "user_card_response"
  | "user_habits"
  | "theme"
  | "habit_category"
  | "card_template"
  | "settings"
  | "path"
  | "path_activity"
  | "habit"
  | "activity"
  | "activity_step"
  | "activity_step_transition"
  | "card"
  | "card_group"
  | "card_group"
  | "card_group_item";

/** Human-readable table labels for media error messages. */
const TABLE_LABELS: Record<string, string> = {
  card: "Carte",
  card_group: "Groupe de cartes",
  activity: "Activité",
  activity_step: "Étape",
  path: "Parcours",
  theme: "Thème",
  settings: "Réglage",
  habit: "Habitude",
  habit_category: "Thématique",
  card_template: "Template",
  user_profile: "Profil utilisateur",
  user_admin_profile: "Profil admin",
};

/** Format a media error message with entity references. */
export function formatMediaError(baseError: string, entry: MediaUrlEntry): string {
  if (!entry.refs || entry.refs.length === 0) return baseError;
  const refStr = entry.refs
    .slice(0, 3)
    .map((r) => {
      const tbl = TABLE_LABELS[r.table] ?? r.table;
      const shortId = r.id.length > 8 ? r.id.slice(0, 8) + "…" : r.id;
      return `${tbl} « ${r.label} » (${shortId})`;
    })
    .join(", ");
  const suffix = entry.refs.length > 3 ? ` (+${entry.refs.length - 3} autres)` : "";
  return `${baseError}\n  → Utilisé par : ${refStr}${suffix}`;
}

export type BackupPayload = {
  version: number;
  exported_at: string;
  scopes_included: BackupScope[];
  auth_users?: unknown[];
  user_role?: unknown[];
  user_admin_profile?: unknown[];
  user_profile?: unknown[];
  user_path_progress?: unknown[];
  user_path_activity_progress?: unknown[];
  user_card_response?: unknown[];
  user_habits?: unknown[];
  theme?: unknown[];
  habit_category?: unknown[];
  card_template?: unknown[];
  settings?: unknown[];
  path?: unknown[];
  path_activity?: unknown[];
  habit?: unknown[];
  activity?: unknown[];
  activity_step?: unknown[];
  activity_step_transition?: unknown[];
  card?: unknown[];
  card_group?: unknown[];
  card_group_item?: unknown[];
};

export type RestoreSelection = Partial<Record<BackupTableKey, boolean>>;

/** Comportement face aux données déjà présentes (import admin authentifié). */
export type ImportConflictStrategy = "wipe_then_import" | "upsert" | "insert_only";

export type ImportBackupResult = {
  ok: boolean;
  errors?: string[];
  generated_credentials?: { email: string; temporary_password: string }[];
  media_migrated?: number;
  media_warnings?: string[];
  media_migration_skipped?: boolean;
};

export type MediaEntityRef = {
  table: string;
  id: string;
  label: string;
};

export type MediaUrlEntry = {
  originalUrl: string;
  bucket: string;
  path: string;
  /** Entities referencing this URL */
  refs: MediaEntityRef[];
};

export type MigrateMediaFileResult = {
  ok: boolean;
  status?: "uploaded" | "skipped";
  newUrl?: string;
  error?: string;
};

/** Construit un message lisible à partir du JSON d'erreur renvoyé par l'Edge Function ou le proxy. */
function summarizeBackupErrorResponse(res: Response, data: unknown): string {
  const status = `HTTP ${res.status}`;
  if (data === null || typeof data !== "object") {
    return `Réponse inattendue (${status}). Corps non JSON ou vide.`;
  }
  const o = data as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of ["error", "message", "msg"] as const) {
    const v = o[key];
    if (typeof v === "string" && v.trim()) parts.push(v.trim());
  }
  const hint = o.hint;
  if (typeof hint === "string" && hint.trim()) parts.push(`Indication : ${hint.trim()}`);
  const errs = o.errors;
  if (Array.isArray(errs)) {
    const strs = errs.filter((x): x is string => typeof x === "string" && !!x.trim());
    if (strs.length) parts.push(strs.join(" ; "));
  }
  const code = o.code;
  if (typeof code === "string" || typeof code === "number") {
    if (parts.length === 0) parts.push(`Code renvoyé : ${code}`);
    else if (!parts.some((p) => p.includes(String(code)))) parts.push(`[${code}]`);
  }
  if (parts.length) return `${parts.join(" — ")} (${status})`;
  return `Aucun détail dans la réponse du serveur (${status}). Ouvrez l'onglet Réseau (F12) → requête « backup » → onglet Réponse.`;
}

const tableKeys: BackupTableKey[] = [
  "auth_users",
  "user_role",
  "user_admin_profile",
  "user_profile",
  "user_path_progress",
  "user_path_activity_progress",
  "user_card_response",
  "user_habits",
  "theme",
  "habit_category",
  "card_template",
  "settings",
  "path",
  "path_activity",
  "habit",
  "activity",
  "activity_step",
  "activity_step_transition",
  "card",
  "card_group",
  "card_group_item",
];

export function keysPresentInPayload(payload: BackupPayload): BackupTableKey[] {
  const keys: BackupTableKey[] = [];
  for (const k of tableKeys) {
    const arr = payload[k] as unknown[] | undefined;
    if (Array.isArray(arr) && arr.length > 0) keys.push(k);
  }
  return keys;
}

// ---------------------------------------------------------------------------
// Media URL extraction (client-side equivalent of media_migration.ts logic)
// ---------------------------------------------------------------------------

const KNOWN_BUCKETS = ["media", "audio", "video", "content"];

function parseStorageUrl(url: string): { bucket: string; path: string } | null {
  const match = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)/);
  if (!match) return null;
  const bucket = decodeURIComponent(match[1]);
  const path = decodeURIComponent(match[2]);
  if (!KNOWN_BUCKETS.includes(bucket)) return null;
  return { bucket, path };
}

function collectStrings(value: unknown, results: Set<string>): void {
  if (typeof value === "string") {
    results.add(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectStrings(item, results);
    return;
  }
  if (value && typeof value === "object") {
    for (const v of Object.values(value)) collectStrings(v, results);
  }
}

/** Human-readable label for a row based on common fields. */
function rowLabel(row: Record<string, unknown>): string {
  for (const key of ["title", "name", "label", "code", "key", "email"]) {
    const v = row[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return String(row.id ?? "?");
}

/** Scan a backup payload and return all remote storage URLs that need migration, with entity refs. */
export function extractMediaUrls(payload: BackupPayload): MediaUrlEntry[] {
  const localUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const unique = new Map<string, MediaUrlEntry>();

  const tablesToScan: BackupTableKey[] = tableKeys;
  for (const tableKey of tablesToScan) {
    const rows = payload[tableKey] as unknown[] | undefined;
    if (!Array.isArray(rows)) continue;

    for (const rawRow of rows) {
      if (!rawRow || typeof rawRow !== "object") continue;
      const row = rawRow as Record<string, unknown>;
      const strings = new Set<string>();
      collectStrings(row, strings);

      const ref: MediaEntityRef = {
        table: tableKey,
        id: String(row.id ?? row.user_id ?? "?"),
        label: rowLabel(row),
      };

      for (const str of strings) {
        const parsed = parseStorageUrl(str);
        if (!parsed) continue;
        if (str.startsWith(localUrl)) continue;

        const existing = unique.get(str);
        if (existing) {
          existing.refs.push(ref);
        } else {
          unique.set(str, {
            originalUrl: str,
            bucket: parsed.bucket,
            path: parsed.path,
            refs: [ref],
          });
        }
      }
    }
  }

  return Array.from(unique.values());
}

// ---------------------------------------------------------------------------
// Per-file media migration API call
// ---------------------------------------------------------------------------

export async function postMigrateMediaFile(
  accessToken: string | null,
  entry: MediaUrlEntry,
  bootstrapSecret?: string,
): Promise<MigrateMediaFileResult> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (bootstrapSecret) headers["x-backup-bootstrap-secret"] = bootstrapSecret;

  const res = await fetch(backupUrl(), {
    method: "POST",
    headers,
    body: JSON.stringify({
      mode: "migrate_media_file",
      originalUrl: entry.originalUrl,
      bucket: entry.bucket,
      path: entry.path,
    }),
  });

  let data: MigrateMediaFileResult;
  try {
    data = (await res.json()) as MigrateMediaFileResult;
  } catch {
    return { ok: false, error: `Réponse non JSON (HTTP ${res.status})` };
  }
  return data;
}

// ---------------------------------------------------------------------------
// Batch rewrite media URLs in database
// ---------------------------------------------------------------------------

export type RewriteMediaUrlsResult = {
  ok: boolean;
  updated_rows?: number;
  warnings?: string[];
  error?: string;
};

export async function postRewriteMediaUrls(
  accessToken: string | null,
  replacements: { oldUrl: string; newUrl: string }[],
  bootstrapSecret?: string,
): Promise<RewriteMediaUrlsResult> {
  if (replacements.length === 0) return { ok: true, updated_rows: 0, warnings: [] };

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (bootstrapSecret) headers["x-backup-bootstrap-secret"] = bootstrapSecret;

  const res = await fetch(backupUrl(), {
    method: "POST",
    headers,
    body: JSON.stringify({ mode: "rewrite_media_urls", replacements }),
  });

  let data: RewriteMediaUrlsResult;
  try {
    data = (await res.json()) as RewriteMediaUrlsResult;
  } catch {
    return { ok: false, error: `Réponse non JSON (HTTP ${res.status})` };
  }
  return data;
}

// ---------------------------------------------------------------------------
// Existing API functions
// ---------------------------------------------------------------------------

export async function fetchBootstrapStatus(bootstrapSecret?: string): Promise<{
  allowFullImport: boolean;
  auth_user_count: number;
}> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (bootstrapSecret) headers["x-backup-bootstrap-secret"] = bootstrapSecret;
  const res = await fetch(backupUrl(), {
    method: "POST",
    headers,
    body: JSON.stringify({ mode: "bootstrap_status" }),
  });
  let data: { allowFullImport?: boolean; auth_user_count?: number; error?: string };
  try {
    data = (await res.json()) as typeof data;
  } catch {
    throw new Error(
      `Réponse non JSON (HTTP ${res.status}). Déployez l'Edge Function « backup » et vérifiez VITE_SUPABASE_URL.`,
    );
  }
  if (!res.ok) throw new Error(data.error || `Erreur statut bootstrap (HTTP ${res.status})`);
  return {
    allowFullImport: !!data.allowFullImport,
    auth_user_count: data.auth_user_count ?? 0,
  };
}

export async function postBootstrapImport(
  payload: BackupPayload,
  bootstrapSecret?: string,
  opts?: { skipMediaMigration?: boolean },
): Promise<ImportBackupResult> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (bootstrapSecret) headers["x-backup-bootstrap-secret"] = bootstrapSecret;
  if (opts?.skipMediaMigration) headers["x-backup-bootstrap-skip-media"] = "1";
  const res = await fetch(backupUrl(), {
    method: "POST",
    headers,
    body: JSON.stringify({ mode: "bootstrap_import", payload }),
  });
  let data: ImportBackupResult & { error?: string; message?: string };
  try {
    data = (await res.json()) as typeof data;
  } catch {
    throw new Error(
      `Réponse non JSON (HTTP ${res.status}). Vérifiez l'Edge Function « backup » et VITE_SUPABASE_URL.`,
    );
  }
  if (!res.ok || data.ok !== true) {
    throw new Error(summarizeBackupErrorResponse(res, data));
  }
  return data;
}

export async function postExport(accessToken: string, scopes: BackupScope[]): Promise<BackupPayload> {
  const res = await fetch(backupUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ mode: "export", scopes }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erreur export");
  return data as BackupPayload;
}

export async function postImport(
  accessToken: string,
  payload: BackupPayload,
  restore: RestoreSelection,
  importConflictStrategy: ImportConflictStrategy = "upsert",
  filename?: string,
): Promise<ImportBackupResult> {
  const res = await fetch(backupUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({
      mode: "import",
      payload,
      restore,
      import_conflict_strategy: importConflictStrategy,
      skip_media_migration: true,
      filename,
    }),
  });
  let data: ImportBackupResult & { error?: string; message?: string };
  try {
    data = (await res.json()) as typeof data;
  } catch {
    throw new Error(`Réponse non JSON (HTTP ${res.status}).`);
  }
  if (!res.ok || data.ok !== true) {
    throw new Error(summarizeBackupErrorResponse(res, data));
  }
  return data;
}

export const RESTORE_SECTION_LABELS: Record<BackupTableKey, string> = {
  auth_users: "Comptes Auth (identités)",
  user_role: "Rôles utilisateur",
  user_admin_profile: "Profils admin",
  user_profile: "Profils application",
  user_path_progress: "Progression parcours",
  user_path_activity_progress: "Progression activités (parcours)",
  user_card_response: "Réponses aux cartes",
  user_habits: "Habitudes utilisateur",
  theme: "Thèmes",
  habit_category: "Thématiques d'habitudes",
  card_template: "Templates de cartes",
  settings: "Réglages (settings)",
  path: "Parcours",
  path_activity: "Liaisons parcours ↔ activités",
  habit: "Habitudes (catalogue lié aux parcours)",
  activity: "Activités",
  activity_step: "Étapes d'activité",
  activity_step_transition: "Transitions entre étapes",
  card: "Cartes",
  card_group: "Groupes de cartes",
  card_group_item: "Items de groupe de cartes",
};
